import type { BreakdownType } from 'components/datatable/datatable.types';
import { EMPTY_ARRAY } from 'constants/defaults';
import {
  CALCULATED_COLUMNS,
  COLUMN_DONUT_PERCENTAGE,
  COLUMN_LOGGED_HOURS,
  COLUMN_NAME,
  COLUMN_NAME_FALLBACK_FOR_HOURS,
  COLUMN_NAMES,
  DEFAULT_DONUT_AND_TIMELINE_COLUMNS,
  DEFAULT_TABLE_COLUMNS,
  HOURS_PAGE_LIMIT,
  ITEMS_WITH_NO_TABLE_COLUMN,
  LOW_DONUT_THRESHOLD,
  NOT_SEEN_YET,
  TIMESPAN_CUSTOM,
} from 'constants/filters';
import { BUDGET_CALCULATION, BUDGET_TYPES } from 'constants/project';
import * as widgetConst from 'constants/widgets';
import { currencyFactory } from 'factories/account';
import { hourTagsFactory } from 'factories/hours';
import { intersection, partialRight, uniq } from 'lodash-es';
import { DateTime } from 'luxon';
import { defaultState, defaultStateId } from 'redux/modules/hourStates.constants';
import type { HourState } from 'redux/modules/hourStates.schema';
import type { HoursDictionary } from 'redux/modules/hours.types';
import type {
  AdditionalWidgetRowValues,
  BudgetDataEntity,
  ClientDataEntity,
  ColumnsProps,
  ColumnsStatus,
  CommonDataEntity,
  DataItemBillingSummary,
  DayDataEntities,
  DayDataEntity,
  DonutDataItem,
  GenericWidgetRow,
  GraphMetadata,
  HoursFilterScope,
  LinkedWorkDataEntity,
  ProjectDataEntities,
  ProjectDataEntity,
  ReportDraft,
  ReportQuery,
  ReportQueryExtended,
  ReportQueryWithoutLegacyFilters,
  ReportSelection,
  TagDataEntity,
  TeamDataEntity,
  UserDataEntity,
  WidgetDraft,
  WidgetMetadataDictionary,
  WidgetWithData,
} from 'redux/modules/superreports.types';
import { WidgetBarScale, WidgetDataType } from 'redux/modules/superreports.types';
import type { TagsDictionary } from 'redux/modules/tags.types';
import { cssVariables } from 'styles';
import type { Currency, StartOfWeek } from 'types/account';
import type { Hour } from 'types/hour';
import { HourLockedReasons } from 'types/hour';
import type { LightProject, ProjectBase } from 'types/project';
import type { EntityDataQuery, PublicReportEntity, ReportBase, ReportEntity } from 'types/reports';
import { TagFilterMode } from 'types/tag';
import type { Team } from 'types/team';
import type { Duration } from 'types/units';
import type { LightUser, UserTimeFormat } from 'types/user';
import type { DeepPartial, UnionToIntersection } from 'types/utility';
import { prefixHexColor } from 'utils/colors';
import { formatCurrency } from 'utils/currency';
import { formatTime } from 'utils/date-helper';
import { durationFromHHMMSS } from 'utils/durations';
import { fromISO } from 'utils/luxon-helpers';
import { findMedian, percentOf, roundedValueToDecimalPlace } from 'utils/numbers';
import { getProjectColor } from 'utils/project';
import type { CostAndDurationSummary, MaxCostAndDurationSummary } from 'utils/summary';
import { getMaxCostAndDurationSummary } from 'utils/summary';
import {
  filterAvailableColumns,
  formatGenericWidgetValues,
  hourToGenericWidgetValues,
  isDataEntity,
  reviseWidgetDraftColumns,
  sortWidgetData,
} from 'utils/superreports';
import { notNull } from 'utils/typesafe';
import { getUtilizationPercentage } from 'utils/utilization';
import type { DataForDate } from './hours';
import {
  reportWidgetHoursLineAsDaysFactory,
  reportWidgetHoursLineAsMonthsFactory,
  reportWidgetHoursLineAsWeeksFactory,
  reportWidgetHoursLineAsYearsFactory,
} from './hours';

export const defaultWidgetStatus: ColumnsStatus = {
  expandedRowIds: [],
  loadingRowIds: [],
  loadedRowIds: [],
  hourIdsByRowId: {},
};

export const newReportWidgetFactory = (
  seed: Partial<WidgetDraft> & Pick<WidgetDraft, 'widget_type'> = { widget_type: '' },
  index?: number
): WidgetDraft => {
  const defaultSequence = 999;
  const columns: Partial<ColumnsProps> = seed.columns ?? {};

  const isHBarWidget = widgetConst.HBAR_WIDGETS.includes(seed.widget_type);
  const defaultSort = isHBarWidget ? COLUMN_LOGGED_HOURS : COLUMN_NAME;
  const defaultOrder = isHBarWidget ? 'desc' : 'asc';

  // Old reports sometimes have an array stored in the options field, so we'll drop those.
  const hasOptions = !!seed.options && !Array.isArray(seed.options);

  const isDonutOrTimeline =
    widgetConst.DONUT_WIDGETS.includes(seed.widget_type) ||
    widgetConst.TIMELINE_WIDGETS.includes(seed.widget_type);

  const names =
    columns.names ??
    (isDonutOrTimeline ? [...DEFAULT_DONUT_AND_TIMELINE_COLUMNS] : [...DEFAULT_TABLE_COLUMNS]);

  // Calculated columns are stripped from `columns.names` when a report is saved, since the API only
  // accepts native column names. Merge them back in so they behave like any other column.
  const options = hasOptions ? seed.options : widgetConst.DEFAULT_WIDGET_OPTIONS;
  const namesWithCalculatedColumns = options?.calculatedColumns?.length
    ? uniq([...names, ...intersection(options.calculatedColumns, CALCULATED_COLUMNS)])
    : names;

  const widget: WidgetDraft = {
    title: seed.title || 'Widget',
    report_id: seed.report_id || undefined,
    sequence: seed.sequence || (index != null ? index + 1 : defaultSequence),
    columns: {
      names: widgetConst.DONUT_WIDGETS.includes(seed.widget_type)
        ? [...namesWithCalculatedColumns, COLUMN_DONUT_PERCENTAGE]
        : namesWithCalculatedColumns,
      sort_by: columns.sort_by || defaultSort,
      order: columns.order || defaultOrder,
    },
    widget_type: seed.widget_type,
    _status: {
      ...defaultWidgetStatus,
      ...seed._status,
    },
    options,
  };

  if (seed.id) {
    widget.id = seed.id;
  } else {
    widget._ref = seed._ref || `unsaved-reference-${Date.now()}-${index ?? 0}-${seed.widget_type}`;
  }

  return widget;
};

// Initiate a draft
export const newReportDraftFactory = (seed: Partial<ReportBase> = {}): ReportDraft => ({
  events: seed.events ?? true,
  id: seed.id,
  hasLoadedInitialData: (seed as ReportDraft).hasLoadedInitialData,
  live: seed.live ?? true,
  name: seed.name ?? '',
  description: seed.description ?? '',
  widgets: seed.widgets?.map((seed, index) => newReportWidgetFactory(seed, index)) ?? [],
  multiCurrency: false,
  downloaded_reports: seed.downloaded_reports ?? [],
});

export const duplicateReportDraftFactory = (seed: Partial<ReportBase>) => {
  const widgets =
    seed.widgets?.map(widget => {
      const { id, _ref, ...rest } = widget as WidgetDraft;
      return rest;
    }) ?? [];
  const { id, ...draft } = seed;

  return newReportDraftFactory({ ...draft, name: `${draft.name} Duplicate`, widgets });
};

// Turns draft into saveable payload
export const saveReportDraftRequestFactory = (
  draft: ReportDraft,
  query: ReportQueryExtended,
  metadata: WidgetMetadataDictionary
): DeepPartial<ReportEntity<string>> => {
  const filters: Partial<ReportQuery['filters']> = { ...query.filters };

  // To show "billed & unbilled", we must omit the billed property
  if (filters.billed == null) {
    delete filters.billed;
  }

  const payload: DeepPartial<ReportEntity<string>> = {
    name: draft.name || '',
    description: draft.description || '',
    span: query.customRangeEnabled ? 'custom' : query.span,
    filters,
    widgets: reviseWidgetDraftColumns(
      draft.widgets.filter(widget => !widget._deleted),
      metadata
    ),
    hour_fields: draft.exportFields?.join(','),
    task_fields: draft.taskExportFields?.join(','),
  };

  if (query.relativeDateRange) {
    payload.date_range = query.relativeDateRange;
  } else {
    payload.from = query.from;
    payload.to = query.to;
  }

  if (draft.id) {
    return {
      id: draft.id,
      ...payload,
    };
  }

  return payload;
};

// SELECTOR STUFF BELOW

export interface ReportListItem {
  createdBy: Pick<LightUser, 'id' | 'name' | 'avatar'>;
  description: string;
  fromTo: string;
  id: number;
  name: string;
  publicReportIds: Array<number>;
  isPinned?: boolean;
  updatedAt: string;
}

export const reportListItemFactory = (
  seed: ReportEntity,
  publicReports: Array<PublicReportListItem> = [],
  pinnedReportIdsSet?: Set<number>
): ReportListItem => {
  const from = DateTime.fromISO(seed.from).toLocaleString({
    ...DateTime.DATE_MED,
    year: undefined,
  });
  const to = DateTime.fromISO(seed.to).toLocaleString({ ...DateTime.DATE_MED, year: undefined });

  return {
    createdBy: {
      id: seed.user.id,
      name: seed.user.name,
      avatar: seed.user.avatar,
    },
    description: seed.description,
    fromTo: seed.span === TIMESPAN_CUSTOM ? `${from} - ${to}` : '',
    id: seed.id,
    isPinned: seed.pinned || pinnedReportIdsSet?.has(seed.id),
    name: seed.name,
    publicReportIds: publicReports.filter(r => r.source?.id === seed.id).map(r => r.id),
    updatedAt: DateTime.fromISO(seed.updated_at).toLocaleString(DateTime.DATE_MED),
  };
};

export interface PublicReportListItem {
  name: string;
  createdAt: string;
  duration: Duration;
  events: boolean;
  fromTo: string;
  id: number;
  live: boolean;
  seenAt: string;
  source?: Pick<PublicReportEntity, 'id' | 'name'>;
  sourceId: number | null;
  url: string;
}

export const publicReportListItemFactory = (
  seed: PublicReportEntity,
  sourceReport?: PublicReportListItem['source']
): PublicReportListItem | null => {
  const createdAt = DateTime.fromISO(seed.created_at).toLocaleString({
    ...DateTime.DATE_MED,
    year: undefined,
  });
  const seenAt = seed.seen_at ? `${DateTime.fromISO(seed.seen_at).toRelative()}` : NOT_SEEN_YET;
  const from = DateTime.fromISO(seed.from).toLocaleString({
    ...DateTime.DATE_MED,
    year: undefined,
  });
  const to = DateTime.fromISO(seed.to).toLocaleString({ ...DateTime.DATE_MED, year: undefined });

  return {
    name: seed.name,
    createdAt,
    duration: durationFromHHMMSS(seed.hours, seed.minutes),
    events: seed.events,
    fromTo: `${from} - ${to}`,
    id: seed.id,
    live: seed.live,
    seenAt,
    source: sourceReport,
    sourceId: seed.report_id ?? null,
    url: seed.public_url,
  };
};

const getLoggedPercentage = (
  totalMinutes: number | undefined,
  loggedMinutesForEntity: number | undefined
) => {
  if (!totalMinutes || !loggedMinutesForEntity) return 0;

  return roundedValueToDecimalPlace(percentOf(loggedMinutesForEntity, totalMinutes), 2);
};

export const reportGenericWidgetFactory = (
  seed: SupportedWidgetEntity,
  resource: string,
  additional: AdditionalWidgetRowValues = {
    maxValues: { minutesLogged: 0 },
    currency: currencyFactory(),
  }
): GenericWidgetRow => {
  const { maxValues, currency: currencyProp, totals, ...otherRelations } = additional;
  // Use provided currency or fall back to a default currency to prevent formatCurrency errors
  const currency = currencyProp ?? currencyFactory();
  const entity = seed as UnionToIntersection<SupportedWidgetEntity>;

  return {
    id: entity.id,
    uri: entity.uri, // For linked_work widget
    resource,
    name: entity.name,
    avatar: entity.avatar ? entity.avatar.medium_retina : null,
    emoji: entity.emoji ? entity.emoji : null,
    color: entity.color ? `#${entity.color}` : null,
    loggedMinutes: entity.duration?.total_minutes,
    plannedMinutes: entity.estimated_duration?.total_minutes,
    loggedPercentage: getLoggedPercentage(totals?.minutesLogged, entity.duration?.total_minutes),
    loggedMoney: formatCurrency(currency, entity.cost?.amount),
    plannedMoney: formatCurrency(currency, entity.estimated_cost?.amount),
    billedMinutes: entity.billed_duration?.total_minutes,
    billedMoney: formatCurrency(currency, entity.billed_cost?.amount),
    unbilledMinutes: entity.unbilled_duration?.total_minutes,
    unbilledMoney: formatCurrency(currency, entity.unbilled_cost?.amount),
    billableHours: entity.billable_duration?.total_hours,
    nonBillableHours: entity.non_billable_duration?.total_hours,
    budgetPercent: !entity.has_recurrence ? entity.budget_percent : undefined,
    internalCost: formatCurrency(currency, entity.internal_cost?.amount),
    profit: formatCurrency(currency, entity.profit?.amount),
    profitability: entity.profitability,
    budgetProgress: !entity.has_recurrence ? entity.budget_progress : undefined,
    budgetType: !entity.has_recurrence ? entity.budget_type : undefined,
    budgetValue: !entity.has_recurrence ? entity.budget : undefined,
    budgetIsPending: entity.budget_calculation === BUDGET_CALCULATION.PENDING,
    capacity: entity.capacity?.total_minutes,
    overtime: entity.overtime?.total_minutes,
    undertime: entity.undertime?.total_minutes,
    utilization: getUtilizationPercentage(
      entity.billable_duration?.total_minutes,
      entity.capacity?.total_minutes
    ),
    ...otherRelations,
  };
};

export const columnNameFactory = (colKey: string, widgetType: string) => {
  const nameColumnKeyMap: Record<string, string> = {
    [widgetConst.WIDGET_PROJECTS_LIST]: 'Project',
    [widgetConst.WIDGET_USERS_LIST]: 'Name',
    [widgetConst.WIDGET_CLIENTS_LIST]: 'Project',
    [widgetConst.WIDGET_LABELS_LIST]: 'Tag',
    [widgetConst.WIDGET_PROJECTS_HBAR]: 'Project',
    [widgetConst.WIDGET_USERS_HBAR]: 'Name',
    [widgetConst.WIDGET_CLIENTS_HBAR]: 'Client',
    [widgetConst.WIDGET_LABELS_HBAR]: 'Tag',
    [widgetConst.WIDGET_LINKED_WORK_LIST]: 'Linked Work',
    // [widgetConst.WIDGET_TEAMS_LIST]: 'Team Name',
  };

  // Special mapping for 'name'
  if (colKey === 'name' && widgetType) {
    return nameColumnKeyMap[widgetType] || COLUMN_NAMES[colKey];
  }
  // All shared col names:
  return (COLUMN_NAMES as Record<string, string>)[colKey] || 'NO_NAME_MAPPED';
};

export const widgetWithGraphDataFactory = (
  widget: Partial<WidgetDraft> & Pick<WidgetDraft, 'widget_type'>,
  entities: DayDataEntities,
  result: Array<string>,
  relations: {
    query: ReportQuery;
    // project: {
    //   budget_type: string;
    //   budget: boolean;
    // } | null;
    project: ProjectDataEntity | null;
    cumulative: boolean;
  },
  entityMetadata: WidgetMetadataDictionary,
  startOfWeek: StartOfWeek,
  currencyLookup: Record<string, Currency>
): WidgetWithData<any, GraphMetadata> => {
  const { query, cumulative = false } = relations;

  if (!query.from || !query.to) {
    const widgetFromFactory = newReportWidgetFactory(widget);
    return {
      ...widgetFromFactory,
      headers: widgetFromFactory.columns.names.map(column => ({
        column,
        name: columnNameFactory(column, widgetFromFactory.widget_type),
        active: column === widgetFromFactory.columns.sort_by,
      })),
      data: [],
      dataEntities: [entityMetadata[widgetFromFactory.widget_type]?.entity],
      metadata: { median: 0, showBilledUnbilled: false },
    };
  }

  const project: BudgetDataEntity = relations.project || {};
  const startDate = fromISO(query.from);
  const end = fromISO(query.to);
  const budget = project && project.budget_type === BUDGET_TYPES.HOUR ? !!project.budget : false;

  const firstDayOfStartDate = startDate.startOf('month');
  const lastDayOfEndDate = end.endOf('month');
  const numMonths = lastDayOfEndDate.diff(firstDayOfStartDate, 'months').months;

  let data: Array<DataForDate>;
  switch (widget.options?.barScale) {
    case WidgetBarScale.Day: {
      const numDays = end.diff(startDate, 'days').days;
      data = reportWidgetHoursLineAsDaysFactory({
        entities,
        startDate: startDate.toISODate(),
        numDays,
        cumulative,
        budget,
        currencyLookup,
      });
      break;
    }

    case WidgetBarScale.Week: {
      const numWeeks = lastDayOfEndDate.diff(firstDayOfStartDate, 'weeks').weeks;
      data = reportWidgetHoursLineAsWeeksFactory({
        entities,
        result,
        startDate: startDate.toISODate(),
        numWeeks,
        cumulative,
        budget,
        startOfWeek,
        currencyLookup,
      });
      break;
    }

    case WidgetBarScale.Month: {
      data = reportWidgetHoursLineAsMonthsFactory({
        entities,
        result,
        startDate: startDate.toISODate(),
        numMonths,
        cumulative,
        budget,
        currencyLookup,
      });
      break;
    }

    case WidgetBarScale.Year: {
      const numYears = lastDayOfEndDate.get('year') - firstDayOfStartDate.get('year');
      data = reportWidgetHoursLineAsYearsFactory({
        entities,
        result,
        startDate: startDate.toISODate(),
        numYears,
        cumulative,
        budget,
        currencyLookup,
      });
      break;
    }

    default: {
      if (numMonths <= 1) {
        const numDays = end.diff(startDate, 'days').days;
        data = reportWidgetHoursLineAsDaysFactory({
          entities,
          startDate: startDate.toISODate(),
          numDays,
          cumulative,
          budget,
          currencyLookup,
        });
      } else if (numMonths < 6) {
        const numWeeks = lastDayOfEndDate.diff(firstDayOfStartDate, 'weeks').weeks;
        data = reportWidgetHoursLineAsWeeksFactory({
          entities,
          result,
          startDate: startDate.toISODate(),
          numWeeks,
          cumulative,
          budget,
          startOfWeek,
          currencyLookup,
        });
      } else {
        data = reportWidgetHoursLineAsMonthsFactory({
          entities,
          result,
          startDate: startDate.toISODate(),
          numMonths,
          cumulative,
          budget,
          currencyLookup,
        });
      }
      break;
    }
  }

  const widgetFromFactory = newReportWidgetFactory(widget);

  let hasBilledHours = false;
  let hasUnbilledHours = false;
  const values = data.map(item => {
    if (item.billedCost || item.billedDuration) {
      hasBilledHours = true;
    }

    if (item.unbilledCost || item.unbilledDuration) {
      hasUnbilledHours = true;
    }

    return item.duration;
  });

  const metadata: GraphMetadata = {
    median: findMedian(values),
    showBilledUnbilled: hasBilledHours || hasUnbilledHours,
  };

  return {
    ...widgetFromFactory,
    headers: widgetFromFactory.columns.names.map(column => {
      return {
        column,
        name: columnNameFactory(column, widgetFromFactory.widget_type),
        active: column === widgetFromFactory.columns.sort_by,
      };
    }),
    data,
    dataEntities: [entityMetadata[widgetFromFactory.widget_type]?.entity],
    metadata,
  };
};

/**
 * Creates a widget row from an hour record.
 */
export const widgetRowHourFactory = (
  hour: Hour<unknown>,
  timeFormat: UserTimeFormat,
  selectedHourIds: Array<number>,
  currencyLookup: Record<string, Currency>
): GenericWidgetRow => {
  const costAndDuration = hourToGenericWidgetValues(hour);
  const partialFormatTime = partialRight(formatTime, timeFormat);
  const currency = currencyLookup[hour.cost.currency_code];
  return {
    resource: 'hour',
    canManage: hour.manage,
    date: DateTime.fromISO(hour.day).toLocaleString(DateTime.DATE_FULL),
    description: hour.note,
    isBilled: hour.billed,
    isLocked: hour.locked && hour.locked_reason === HourLockedReasons.Locked,
    isInvoiced: hour.locked && hour.locked_reason === HourLockedReasons.Invoiced,
    isHour: true,
    profit: formatCurrency(currency, hour.profit?.amount),
    profitability: hour.profitability,
    internalCost: formatCurrency(currency, hour.internal_cost?.amount),
    isSelected: selectedHourIds.includes(hour.id),
    from: hour.from && partialFormatTime(hour.from),
    to: hour.to && partialFormatTime(hour.to),
    timestamps: hour.timestamps
      ? hour.timestamps.map(({ from, to, id }) => ({
          id,
          from: partialFormatTime(from),
          to: partialFormatTime(to),
        }))
      : EMPTY_ARRAY,
    markedAsUnbillable:
      (!hour.billable && !!hour.cost.amount) || (!hour.billable && !!hour.estimated_cost?.amount),
    ...formatGenericWidgetValues(costAndDuration, currency),
  };
};

export const widgetRowHourListFactory = ({
  widgetType,
  rowId,
  hourEntities,
  hourResult,
  labelEntities,
  projectEntities,
  userEntities,
  sortBy,
  order,
  selection,
  timeFormat,
  filters,
  currencyLookup,
  hourIdsByRowId,
}: {
  widgetType: string;
  rowId: number;
  hourEntities: {
    [id: number]: Hour<number>;
  };
  hourResult: Array<number>;
  labelEntities: {
    [id: number]: TagDataEntity;
  };
  projectEntities: ProjectDataEntities;
  userEntities: {
    [id: number]: UserDataEntity;
  };
  teamEntities: {
    [id: number]: TeamDataEntity;
  };
  sortBy: string;
  order: 'asc' | 'desc';
  selection: {
    hourIds: Array<number>;
  };
  timeFormat: UserTimeFormat;
  filters: ReportQuery['filters'];
  currencyLookup: Record<string, Currency>;
  hourIdsByRowId?: Record<number, Array<number>>; // For linked_work widget
}): Array<GenericWidgetRow> => {
  const hourSortBy = sortBy === COLUMN_NAME ? COLUMN_NAME_FALLBACK_FOR_HOURS : sortBy;

  // Hoist outside the filter loop: filters.label_ids and labelEntities are constant across
  // all hours. Expand each child label ID to also include its parent so that entries tagged
  // directly with only the parent label are still matched.
  const expandedFilterLabels = filters.label_ids.length
    ? new Set(
        filters.label_ids.flatMap(labelId => {
          const parentId = (labelEntities[labelId] as TagDataEntity | undefined)?.parent_id;
          return parentId ? [labelId, parentId] : labelId;
        })
      )
    : null;

  return hourResult
    .filter(hourId => {
      const hour = hourEntities[hourId];
      const projectIsPartOfReport = !!projectEntities[hour.project];

      // We sometimes see `hours` loaded from Calendar views in here, but they shouldn't be included
      // unless they also match a projectEntity in the report data
      // (otherwise, you'll see hours from unrelated projects in this list)
      if (!projectIsPartOfReport) {
        return false;
      }

      // If some hour entries for a project are updated to no longer match the filters but there
      // are still other hours left in the project, the check above won't do - the hour entries will
      // still be displayed. We need to perform optimistic client-side filtering by billed state/tag
      // (the properties that can be batch edited) to ensure they are hidden correctly
      if (filters.billed !== null && hour.billed !== filters.billed) {
        return false;
      }

      const { label_ids } = filters;
      const isFilteringByUntaggedOnly = label_ids.length === 1 && label_ids[0] === 0;
      const hourHasBeenTagged = !!hour.label_ids.length;

      if (isFilteringByUntaggedOnly && hourHasBeenTagged) {
        return false;
      } else if (filters.label_ids.length) {
        // Use expandedFilterLabels (hoisted above) which expands each filter child ID to
        // also include its parent. This covers hours tagged directly with the parent label
        // before children were added (edge case: entry pre-dates child tag creation).
        // The parentId check below additionally covers hours whose child tag should match
        // a parent filter ID (e.g. filter stored from URL before dropdown expansion ran).
        const hourTagsDoNotMatchFilter = !hour.label_ids.some(labelId => {
          const parentId = labelEntities[labelId]?.parent_id;
          return (
            expandedFilterLabels!.has(labelId) ||
            (parentId != null && expandedFilterLabels!.has(parentId))
          );
        });

        if (hourTagsDoNotMatchFilter && hourHasBeenTagged) {
          return false;
        }
      }

      if (widgetConst.WIDGET_TYPE_USERS.includes(widgetType)) {
        const hourMatchesUser = hour.user.id === rowId;
        return hourMatchesUser;
      }

      if (widgetConst.WIDGET_TYPE_PROJECTS.includes(widgetType)) {
        return hour.project === rowId;
      }

      if (widgetConst.WIDGET_TYPE_LABELS.includes(widgetType)) {
        const matchesLabelId = hour.label_ids.includes(rowId);
        const matchesVirtualLabel = !hour.label_ids.length && rowId === 0;
        return matchesLabelId || matchesVirtualLabel;
      }

      // For linked_work, use the hourIdsByRowId mapping since hours don't have external_link info
      if (widgetConst.WIDGET_TYPE_LINKED_WORK.includes(widgetType)) {
        const rowHourIds = hourIdsByRowId?.[rowId] || [];
        return rowHourIds.includes(hourId);
      }

      // @todo widgetRowHourListFactory for widgetType
      return false;
    })
    .sort((a, b) => sortWidgetData(hourEntities[a], hourEntities[b], hourSortBy, order))
    .map<GenericWidgetRow>(hourId => {
      const hour = hourEntities[hourId];
      // Attempt to use user/project directly, and fall back to embed on hours if none exist
      // Can happen if project/user is deleted? Maybe?
      const project = projectEntities[hour.project] as ProjectBase;
      const user = userEntities[hour.user.id] || hour.user;

      const labels = hourTagsFactory(hour.label_ids, labelEntities);

      const sharedRow: GenericWidgetRow = {
        ...widgetRowHourFactory(hour, timeFormat, selection.hourIds, currencyLookup),
        id: hour.id,
        name: hour.note || 'Untitled',
        color: prefixHexColor(getProjectColor(project)),
        project,
      };

      // Listing hours in individual users should include project name
      if (widgetConst.WIDGET_TYPE_USERS.includes(widgetType)) {
        return {
          ...sharedRow,
          name: `${project.name}`,
          labels,
        };
      }

      // Listing hours on individual projects should include user's name
      if (
        widgetConst.WIDGET_CLIENTS_LIST === widgetType ||
        widgetConst.WIDGET_TYPE_PROJECTS.includes(widgetType) ||
        widgetConst.WIDGET_TYPE_LABELS.includes(widgetType) ||
        widgetConst.WIDGET_TYPE_LINKED_WORK.includes(widgetType)
      ) {
        return {
          ...sharedRow,
          avatar: user.avatar ? user.avatar.medium_retina : null,
          name: user.name,
          labels,
        };
      }

      return sharedRow;
    });
};

export const exportPreviewHourFactory = (
  hour: Hour<LightProject>,
  timeFormat: UserTimeFormat,
  currencyLookup: Record<string, Currency>,
  hourStates: Record<number, HourState>,
  tags: TagsDictionary,
  teams: Array<Team>
) => {
  const costAndDuration = hourToGenericWidgetValues(hour);
  const partialFormatTime = partialRight(formatTime, timeFormat);
  const currency = currencyLookup[hour.cost.currency_code];

  const state = hourStates[hour.state?.id || defaultStateId] ?? hour.state ?? defaultState;
  const hourTags = hourTagsFactory(hour.label_ids, tags);
  const userTeams = teams.filter(t => t.user_ids?.includes(hour.user.id)).map(t => t.name);

  return {
    id: hour.id,
    date: DateTime.fromISO(hour.day).toLocaleString(DateTime.DATE_FULL),
    note: hour.note,
    isBilled: hour.billed,
    isLocked: hour.locked && hour.locked_reason === HourLockedReasons.Locked,
    isInvoiced: hour.locked && hour.locked_reason === HourLockedReasons.Invoiced,
    profit: formatCurrency(currency, hour.profit?.amount),
    profitability: hour.profitability,
    internalCost: formatCurrency(currency, hour.internal_cost?.amount),
    forecastId: hour.forecast_id,
    state,
    tags: hourTags,
    user: hour.user,
    teams: userTeams,
    project: hour.project,
    from: hour.from && partialFormatTime(hour.from),
    to: hour.to && partialFormatTime(hour.to),
    timestamps: hour.timestamps
      ? hour.timestamps.map(({ from, to, id }) => ({
          id,
          from: partialFormatTime(from),
          to: partialFormatTime(to),
        }))
      : EMPTY_ARRAY,
    markedAsUnbillable:
      (!hour.billable && !!hour.cost.amount) || (!hour.billable && !!hour.estimated_cost?.amount),
    ...formatGenericWidgetValues(costAndDuration, currency),
  };
};

export type ExportPreviewRow = ReturnType<typeof exportPreviewHourFactory>;

export interface RelationsType {
  hourEntities: HoursDictionary;
  hourResult: Array<number>;
  projectResult?: Array<number>;
  projectEntities?: ProjectDataEntities;
  maxValues: MaxCostAndDurationSummary;
  labelEntities: {
    [id: number]: TagDataEntity;
  };
  userEntities: {
    [id: number]: UserDataEntity;
  };
  teamEntities: {
    [id: number]: TeamDataEntity;
  };
  selection: ReportSelection;
}

// We can pass dictionary with different entities here
export type SupportedWidgetEntity =
  | UserDataEntity
  | TagDataEntity
  | ClientDataEntity
  | ProjectDataEntity
  | DayDataEntity
  | TeamDataEntity
  | LinkedWorkDataEntity;

export interface WidgetEntities {
  [key: number]: SupportedWidgetEntity;
}

export const widgetWithDataFactory = (
  widget: WidgetDraft,
  entities: WidgetEntities = {},
  result: Array<number> = [],
  relations: RelationsType,
  timeFormat: UserTimeFormat,
  filters: ReportQuery['filters'],
  metadata: WidgetMetadataDictionary,
  currencyLookup: Record<string, Currency>,
  totals: CostAndDurationSummary
): WidgetWithData => {
  const {
    hourEntities = {},
    hourResult = [],
    projectEntities = {},
    projectResult = [],
    maxValues = {},
    // Relations needed for hours lists
    labelEntities = {},
    userEntities = {},
    teamEntities = {},
    selection = { hourIds: [] },
  } = relations;
  let ids = result;
  if (
    [widgetConst.WIDGET_LABELS_LIST, widgetConst.WIDGET_LABELS_HBAR].includes(widget.widget_type)
  ) {
    // Since we only process non-child tags in our top loop, we need to add parent ids when only
    // some of the child tags are selected, since we'd skip those tags otherwise
    ids = uniq(
      result.flatMap(tagId => {
        const tag = entities[tagId] as TagDataEntity;
        if (tag?.parent_id) {
          return [tag.parent_id, tagId];
        }
        return tagId;
      })
    );
  }

  const rows = ids
    // make sure selected entries exist + create copy for mutating .sort
    .filter(id => !!entities[id])
    .sort((a, b) => {
      const { sort_by, order } = widget.columns;
      return sortWidgetData(
        entities[a] as CommonDataEntity,
        entities[b] as CommonDataEntity,
        sort_by,
        order
      );
    })
    .map<GenericWidgetRow | null>(id => {
      // If row is expanded, include _ANY_ hour that matches the widget type + row id.
      const expandToShowHours = widget._status.expandedRowIds.includes(id);
      const hours = expandToShowHours
        ? widgetRowHourListFactory({
            widgetType: widget.widget_type,
            rowId: id,
            hourEntities,
            hourResult,
            labelEntities,
            userEntities,
            projectEntities,
            teamEntities,
            sortBy: widget.columns.sort_by,
            order: widget.columns.order,
            selection,
            timeFormat,
            filters,
            currencyLookup,
            hourIdsByRowId: widget._status.hourIdsByRowId,
          })
        : [];

      switch (widget.widget_type) {
        case widgetConst.WIDGET_LABELS_HBAR:
        case widgetConst.WIDGET_LABELS_LIST: {
          const tag = entities[id];
          if ((tag as TagDataEntity)?.parent_id) {
            return null;
          }
          // Skip parent tags that only exist in the global tag store (no report data).
          // When a large label filter is applied (e.g. "All Tags"), filters.label_ids
          // drives labelResult so every parent tag is iterated — including those the
          // server did not return because they had no hours in the current period.
          // Without this guard those tags render as ghost rows showing "0 matching hours".
          if (!isDataEntity(tag)) {
            return null;
          }
          const dataTag = tag as TagDataEntity;
          // filtered at bottom, don't have child tags at parent level
          // Calculate relative max values for projects nested under clients.
          // NB: Values are relative to ALL projects, instead of relative to projects under a client.
          const maxValuesForLabels = getMaxCostAndDurationSummary(result, entities);

          const childTags = (dataTag.children || [])
            // Only include tags specifically selected, and that actually exists.
            // Patch over a weird issue I'm not 100% sure why exists,
            // ref https://memoryai.slack.com/archives/C24C3J73P/p1526492688000152
            .filter(tagId => isDataEntity(entities[tagId]))
            .sort((a, b) => {
              const { sort_by, order } = widget.columns;
              return sortWidgetData(
                entities[a] as CommonDataEntity,
                entities[b] as CommonDataEntity,
                sort_by,
                order
              );
            })
            .map(tagId => {
              // We might want to consider expandedSubRowIds to avoid collisions IF
              // we introduce widgest that can be expanded on each level.
              const expandProjectToShowHours = widget._status.expandedRowIds.includes(tagId);
              const tagHours = expandProjectToShowHours
                ? widgetRowHourListFactory({
                    widgetType: widget.widget_type,
                    rowId: tagId,
                    hourEntities,
                    hourResult,
                    labelEntities,
                    userEntities,
                    projectEntities,
                    teamEntities,
                    sortBy: widget.columns.sort_by,
                    order: widget.columns.order,
                    selection,
                    timeFormat,
                    filters,
                    currencyLookup,
                    hourIdsByRowId: widget._status.hourIdsByRowId,
                  })
                : [];
              const childTag = entities[tagId] as TagDataEntity;
              const childTagCurrency = currencyLookup[childTag.cost?.currency_code ?? ''];
              return reportGenericWidgetFactory(childTag, 'tag', {
                maxValues: maxValuesForLabels,
                hours: tagHours,
                indentedRow: true,
                currency: childTagCurrency,
                totals,
              });
            });

          const tagCurrency = currencyLookup[dataTag.cost?.currency_code ?? ''];
          return reportGenericWidgetFactory(dataTag, 'tag', {
            rows: childTags.length ? childTags : undefined,
            hours: childTags.length ? undefined : hours,
            maxValues,
            currency: tagCurrency,
            totals,
          });
        }
        case widgetConst.WIDGET_CLIENTS_LIST: {
          const client = entities[id] as ClientDataEntity;
          // Calculate relative max values for projects nested under clients.
          // NB: Values are relative to ALL projects, instead of relative to projects under a client.
          const maxValuesForProjects = getMaxCostAndDurationSummary(projectResult, projectEntities);

          const projects = client.projects
            ?.filter(projectId => projectResult.includes(projectId))
            .sort((a, b) => {
              const { sort_by, order } = widget.columns;
              return sortWidgetData(projectEntities[a], projectEntities[b], sort_by, order);
            })
            .map(projectId => {
              // We might want to consider expandedSubRowIds to avoid collisions IF
              // we introduce widgest that can be expanded on each level.
              const expandProjectToShowHours = widget._status.expandedRowIds.includes(projectId);
              const projectHours = expandProjectToShowHours
                ? widgetRowHourListFactory({
                    widgetType: widget.widget_type,
                    rowId: projectId,
                    hourEntities,
                    hourResult,
                    labelEntities,
                    userEntities,
                    projectEntities,
                    teamEntities,
                    sortBy: widget.columns.sort_by,
                    order: widget.columns.order,
                    selection,
                    timeFormat,
                    filters,
                    currencyLookup,
                    hourIdsByRowId: widget._status.hourIdsByRowId,
                  })
                : [];
              const projectCurrency =
                currencyLookup[projectEntities[projectId]?.cost?.currency_code ?? ''];
              return reportGenericWidgetFactory(projectEntities[projectId], 'project', {
                maxValues: maxValuesForProjects,
                hours: projectHours,
                currency: projectCurrency,
                totals,
              });
            });
          const clientCurrency = currencyLookup[client.cost?.currency_code ?? ''];
          return reportGenericWidgetFactory(client, 'client', {
            projects,
            maxValues,
            currency: clientCurrency,
            totals,
          });
        }
        // Generic lists work well for most cases:
        default: {
          // Use entity currency if available, otherwise fall back to totals currency
          const entityCurrency =
            currencyLookup[entities[id]?.cost?.currency_code ?? ''] ?? totals.currency;
          return reportGenericWidgetFactory(
            entities[id],
            widget.widget_type.split(/\W+/)[0].toLowerCase(),
            {
              totals,
              maxValues,
              hours,
              currency: entityCurrency,
            }
          );
        }
      }
    })
    .filter(notNull);
  const widgetFromFactory = newReportWidgetFactory(widget);

  const widgetColumnNamesFiltered = filterAvailableColumns(
    widget.columns.names,
    widget.widget_type,
    filters.billed
  );

  return {
    ...widgetFromFactory,
    headers: widgetColumnNamesFiltered
      .filter(name => !ITEMS_WITH_NO_TABLE_COLUMN.includes(name))
      .map(column => {
        return {
          column,
          name: columnNameFactory(column, widget.widget_type),
          active: column === widget.columns.sort_by,
        };
      }),
    rows,
    dataEntities: [metadata[widget.widget_type]?.entity],
    title: metadata[widget.widget_type]?.title || widget.title,
  };
};

// Filter queries for fetching data on report draft
export const dataQueryFilterRequestFactory = (
  query: ReportQueryWithoutLegacyFilters,
  breakdownType: BreakdownType = 'day'
) => {
  const { from, to, filters, group_only = true, client_ids } = query;
  const {
    team_ids,
    label_ids,
    project_ids,
    project_ids_not_in,
    user_ids,
    state_ids,
    label_filter,
    external_link_filter,
    sync_status_filter,
  } = filters;
  // Can be 'all', 'active', 'archived' or array of project ids
  const projectIdsAsString = Array.isArray(project_ids) ? project_ids.join(',') : project_ids;
  return {
    since: from,
    until: to,
    // Need to submit as strings, not arrays:
    team_ids: team_ids.join(','),
    label_ids: label_ids.join(','),
    project_ids: projectIdsAsString,
    // Only a group token leaves anything to carve out; beside an explicit id list the key would
    // subtract from the very ids being asked for.
    project_ids_not_in:
      !Array.isArray(project_ids) && project_ids_not_in?.length
        ? project_ids_not_in.join(',')
        : undefined,
    user_ids: user_ids.join(','),
    state_ids: state_ids.join(','),
    label_filter: label_filter || TagFilterMode.OR,
    external_link_filter,
    sync_status_filter,
    group_only,
    client_ids: client_ids ? client_ids.join(',') : undefined,
    interval: breakdownType,
  };
};

// Filter queries for fetching hours on report draft
// @todo test this.. not 100% sure on pagination stuffs
export const hourQueryFilterRequestFactory = (
  query: ReportQueryWithoutLegacyFilters,
  params: {
    id: number;
    uri?: string; // For linked_work widget, we use URI instead of ID
    filterScope: HoursFilterScope;
    hourEntities: {
      [id: number]: Hour<number>;
    };
    hourResult: Array<number>;
    limit?: number;
  }
): EntityDataQuery => {
  const {
    id,
    uri,
    filterScope,
    hourEntities = {},
    hourResult = [],
    limit = HOURS_PAGE_LIMIT,
  } = params;

  // Determine how many existing results we have that match the hours we're looking for,
  // and avoid re-loading those from server by passing number via offset.
  const offset = hourResult.filter(hourId => {
    const hour = hourEntities[hourId];

    if (filterScope === 'user_ids') {
      const hourMatchesUser = hour.user.id === id;
      // only check if the hour matches project if we're attempting to filter by project_id(s)
      if (query.filters.project_ids.length > 0) {
        // In some cases, hours are already loaded for this user_id (ie, via Calendar)
        // so only count hours on the projectId specified to get correct offset.
        const hourMatchesProject = Array.isArray(query.filters.project_ids)
          ? query.filters.project_ids.includes(hour.project)
          : query.filters.project_ids === 'all';
        // prepare for normalization to maybe take hour.user away
        return hourMatchesUser && hourMatchesProject;
      }
      return hourMatchesUser;
    }
    if (filterScope === 'label_ids') {
      const matchesLabelId = hour.label_ids.includes(id);
      const matchesVirtualLabel = !hour.label_ids.length && id === 0;
      return matchesLabelId || matchesVirtualLabel;
    }
    if (filterScope === 'project_ids') {
      return hour.project === id; // expects normalized hour.project
    }
    // For external_link_filter, we can't easily match hours client-side
    // since hour entities don't have external_links loaded
    if (filterScope === 'external_link_filter') {
      return false;
    }

    return false;
  }).length;

  // console.warn('Using', { limit, offset }, 'that requires backend API compatibility to work');

  // For external_link_filter, we pass the URI with prefix instead of ID
  const filterValue = filterScope === 'external_link_filter' && uri ? `uri:${uri}` : id.toString();

  return {
    // Many filters are same as dataQuery...
    ...dataQueryFilterRequestFactory(query),
    // With a few special keys for querying hours
    scope: 'events' as const,
    limit,
    offset,
    [params.filterScope as string]: filterValue, // server freaks out if passed int
    sort: 'day',
    order: 'desc',
    group_only: true,
  };
};

export function getSubHeading(entity: CommonDataEntity): string | undefined {
  const intersection = entity as UnionToIntersection<SupportedWidgetEntity>;
  return intersection.client?.name || intersection.name;
}

export const widgetWithDonutDataFactory = (
  widget: WidgetDraft,
  entities: WidgetEntities = {},
  entityMetadata: WidgetMetadataDictionary,
  currencyLookup: Record<string, Currency>,
  filters?: ReportQuery['filters']
): WidgetWithData<DonutDataItem> => {
  let sum = 0;
  const getValue: (entity: CommonDataEntity) => number =
    widget.options?.dataType === 'money'
      ? entity => entity.cost?.amount ?? 0
      : entity => entity.duration?.total_hours ?? 0;

  const getBilledValue: (entity: CommonDataEntity) => number =
    widget.options?.dataType === 'money'
      ? entity => entity.billed_cost?.amount ?? 0
      : entity => entity.billed_duration?.total_hours ?? 0;

  const getUnbilledValue: (entity: CommonDataEntity) => number =
    widget.options?.dataType === 'money'
      ? entity => entity.unbilled_cost?.amount ?? 0
      : entity => entity.unbilled_duration?.total_hours ?? 0;

  let data = Object.values(entities)
    .map((entity: UnionToIntersection<SupportedWidgetEntity>): DonutDataItem => {
      const isLabelsDonut = widget.widget_type === widgetConst.WIDGET_LABELS_DONUT;
      // equivalent to "any"
      const filterHasNoLabels = filters?.label_ids.length === 0;
      const flattenedEntityIds = [entity.parent_id, entity.id, entity.children].flat();
      // Untagged is the synthetic id-0 tag; only the filter decides whether its slice shows:
      // visible when no tag filter is set or the Untagged sentinel (0) is selected, hidden
      // (and excluded from the slice sum) otherwise.
      const isUntaggedEntity = entity.id === 0;
      const matchesFilter =
        filterHasNoLabels ||
        (isUntaggedEntity
          ? !!filters?.label_ids.includes(0)
          : !!intersection(filters?.label_ids, flattenedEntityIds).length);
      // Untagged can never be a parent whose children render as their own slices, so it is
      // exempt from the parent-skip rule below.
      const isParentWithChildren =
        !isUntaggedEntity && entity.parent_id == null && !!entity.children?.length;
      // skip parent tags unless it has no children, these get filtered out automatically below
      if ((!matchesFilter || isParentWithChildren) && isLabelsDonut) {
        return {
          name: entity.name!,
          subHeading: undefined,
          value: 0,
          percentOfTotal: 0,
          color: undefined,
          billingSummary: undefined,
        };
      }

      const value = getValue(entity);
      const billedValue = getBilledValue(entity);
      const unbilledValue = getUnbilledValue(entity);
      sum += value;

      const billingSummary: DataItemBillingSummary | undefined =
        billedValue || unbilledValue
          ? {
              billedAmount: billedValue,
              billedPercent: 0,
              unbilledAmount: unbilledValue,
              unbilledPercent: 0,
            }
          : undefined;
      const parentEntity = entities[entity.parent_id!];
      return {
        name: entity.name!,
        subHeading: getSubHeading(parentEntity ?? entity),
        value,
        percentOfTotal: 0,
        color: typeof entity.color === 'string' ? `#${entity.color}` : undefined,
        billingSummary,
        currency: billingSummary
          ? currencyLookup[
              entity.billed_cost?.currency_code || entity.unbilled_cost?.currency_code || ''
            ]
          : undefined,
      };
    })
    .filter(d => d.value > 0)
    .map(item => {
      item.percentOfTotal = (item.value / sum) * 100;
      if (item.billingSummary) {
        item.billingSummary.billedPercent =
          ((item.billingSummary.billedAmount ?? 0) / item.value) * 100;
        item.billingSummary.unbilledPercent =
          ((item.billingSummary.unbilledAmount ?? 0) / item.value) * 100;
      }
      return item;
    })
    .sort((a, b) => b.value - a.value);

  // If multiple entries would take up less than 20 degrees of the donut chart each,
  // merge them into the "Other" category unless we have a large data set.
  // Otherwise lower the barrier to below 0.5%
  const thresholdFactor = data.length > 20 ? LOW_DONUT_THRESHOLD : 20 / 360;
  const threshold = sum * thresholdFactor;
  const thresholdIndex = data.findIndex(({ value }) => value < threshold);

  if (thresholdIndex !== -1 && thresholdIndex + 2 <= data.length) {
    const mergedData = data.slice(thresholdIndex).reduce(
      (other, item) => {
        other.mergedItems!.push(item);
        other.value += item.value;
        other.percentOfTotal += item.percentOfTotal;
        if (!other.billingSummary && item.billingSummary) {
          other.billingSummary = {
            billedAmount: 0,
            billedPercent: 0,
            unbilledAmount: 0,
            unbilledPercent: 0,
          };
        }
        if (item.billingSummary) {
          other.billingSummary!.billedAmount! += item.billingSummary?.billedAmount ?? 0;
          other.billingSummary!.unbilledAmount! += item.billingSummary?.unbilledAmount ?? 0;
        }
        return other;
      },
      { name: 'Other', value: 0, mergedItems: [], percentOfTotal: 0 }
    );

    if (mergedData.billingSummary) {
      mergedData.billingSummary.billedPercent =
        ((mergedData.billingSummary.billedAmount ?? 0) / mergedData.value) * 100;
      mergedData.billingSummary.unbilledPercent =
        ((mergedData.billingSummary.unbilledAmount ?? 0) / mergedData.value) * 100;
    }
    data = data.slice(0, thresholdIndex).concat([mergedData]);
  }

  return {
    ...widget,
    threshold_factor: thresholdFactor,
    data,
    dataEntities: [entityMetadata[widget.widget_type]?.entity],
    title: entityMetadata[widget.widget_type]?.title || widget.title,
  };
};

export const widgetWithBilledVsUnbilledDataFactory = (
  widget: WidgetDraft,
  totals: CostAndDurationSummary,
  entityMetadata: WidgetMetadataDictionary
): WidgetWithData<DonutDataItem> => {
  let billedSum: number;
  let unbilledSum: number;
  if (widget.options?.dataType === WidgetDataType.Money) {
    billedSum = totals.costBilled;
    unbilledSum = totals.costUnbilled;
  } else {
    billedSum = totals.minutesBilled / 60;
    unbilledSum = totals.minutesUnbilled / 60;
  }

  const totalSum =
    widget.options?.dataType === WidgetDataType.Money
      ? totals.costLogged
      : totals.minutesLogged / 60;
  const billedPercent = (billedSum / totalSum) * 100;
  const unbilledPercent = (unbilledSum / totalSum) * 100;

  const data: Array<DonutDataItem> = [
    {
      name: 'Billed',
      value: billedSum,
      percentOfTotal: billedPercent,
      color: cssVariables.widgetBilledColor,
      currency: totals.currency,
    },
    {
      name: 'Unbilled',
      value: unbilledSum,
      percentOfTotal: unbilledPercent,
      color: cssVariables.widgetOtherColor,
      currency: totals.currency,
    },
  ];

  return {
    ...widget,
    data,
    dataEntities: [entityMetadata[widget.widget_type]?.entity],
    title: entityMetadata[widget.widget_type]?.title || widget.title,
  };
};

export const widgetWithBillableVsNonBillableDataFactory = (
  widget: WidgetDraft,
  totals: CostAndDurationSummary,
  entityMetadata: WidgetMetadataDictionary
): WidgetWithData<DonutDataItem> => {
  const billableSum = (totals.minutesBillable ?? 0) / 60;
  const nonBillableSum = (totals.minutesNonBillable ?? 0) / 60;

  const totalSum = totals.minutesLogged / 60;
  const billablePercent = (billableSum / totalSum) * 100;
  const nonBillablePercent = (nonBillableSum / totalSum) * 100;

  const data: Array<DonutDataItem> = [
    {
      name: 'Billable',
      value: billableSum,
      percentOfTotal: billablePercent,
      color: cssVariables.widgetBillableColor,
    },
    {
      name: 'Non-billable',
      value: nonBillableSum,
      percentOfTotal: nonBillablePercent,
      color: cssVariables.widgetOtherColor,
    },
  ];

  return {
    ...widget,
    data,
    dataEntities: [entityMetadata[widget.widget_type]?.entity],
    title: entityMetadata[widget.widget_type]?.title || widget.title,
  };
};
